import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DINOS, DINO_ORDER } from '../data/dinos';
import { CommsBox } from '../objects/CommsBox';
import { Music } from '../objects/Music';

/**
 * Brief title-screen intro. Plays once after Preload, before the level
 * select. Shows the ASTRODINOS logo over a starfield, runs a 2-line comms
 * exchange between Mika and Bosco, then transitions to CharacterSelect.
 *
 * Skippable with any key/click.
 */
export class TitleScene extends Phaser.Scene {
  private done = false;

  constructor() {
    super('Title');
  }

  create() {
    this.done = false;
    this.cameras.main.setBackgroundColor(0x05070f);

    // Upbeat menu anthem — keeps playing through Title → CharacterSelect
    // (and Leaderboard) without restarting, since Music.start short-circuits
    // when the same key is already in flight.
    Music.start('menu');

    // Starfield with planet-color glints.
    for (let i = 0; i < 110; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(0, GAME_HEIGHT);
      const planetColor = DINOS[DINO_ORDER[i % 4]].stripe;
      const star = this.add
        .image(x, y, 'px')
        .setAlpha(Phaser.Math.FloatBetween(0.25, 1))
        .setTint(Math.random() < 0.18 ? planetColor : 0xffffff);
      this.tweens.add({
        targets: star,
        alpha: { from: star.alpha, to: star.alpha * 0.4 },
        duration: Phaser.Math.Between(1200, 3000),
        yoyo: true,
        repeat: -1,
      });
    }

    // Title pop — drops in from above with a bounce.
    const title = this.add
      .text(GAME_WIDTH / 2, -40, 'ASTRODINOS', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '34px',
        color: '#ffe23a',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: title,
      y: GAME_HEIGHT / 2 - 40,
      duration: 700,
      ease: 'Bounce.easeOut',
    });

    // Subtitle slides in below the title.
    const subtitle = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 8, 'four planets. one team.', {
        fontFamily: '"VT323", monospace',
        fontSize: '14px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({
      targets: subtitle,
      alpha: 1,
      delay: 700,
      duration: 600,
    });

    // Drifting pixel particles around the subtitle — mirrors the DriftingPixels
    // component from the marketing site. Each pixel fades in while drifting
    // upward then fades out and resets, looping indefinitely. Previous values
    // (1-3px @ 0.12-0.45 alpha) were so faint they were effectively invisible
    // on most displays — bumped up so the effect actually reads.
    const planetTints = DINO_ORDER.map((id) => DINOS[id].stripe);
    const createDriftPixel = (startDelay: number) => {
      const spawnX = GAME_WIDTH / 2 + Phaser.Math.FloatBetween(-110, 110);
      const spawnY = GAME_HEIGHT / 2 - 8 + Phaser.Math.FloatBetween(-12, 12);
      const size = Math.ceil(Phaser.Math.FloatBetween(2, 4));
      const driftX = Phaser.Math.FloatBetween(-30, 30);
      const driftY = Phaser.Math.FloatBetween(-50, -20);
      const peakAlpha = Phaser.Math.FloatBetween(0.55, 1);
      const dur = Phaser.Math.Between(2200, 4200);
      // 35% of the pixels get tinted to a planet stripe color, the rest
      // stay white — the splash of color sells the "four planets" theme.
      const tinted = Math.random() < 0.35;
      const color = tinted
        ? planetTints[Phaser.Math.Between(0, planetTints.length - 1)]
        : 0xffffff;

      const g = this.add.graphics().setAlpha(0).setDepth(5);
      g.fillStyle(color, 1);
      g.fillRect(0, 0, size, size);
      g.setPosition(spawnX, spawnY);

      const loop = () => {
        g.setPosition(spawnX, spawnY);
        g.setAlpha(0);
        this.tweens.add({
          targets: g,
          alpha: peakAlpha,
          x: spawnX + driftX * 0.4,
          y: spawnY + driftY * 0.4,
          duration: dur * 0.35,
          onComplete: () => {
            this.tweens.add({
              targets: g,
              alpha: 0,
              x: spawnX + driftX,
              y: spawnY + driftY,
              duration: dur * 0.65,
              onComplete: loop,
            });
          },
        });
      };

      this.time.delayedCall(700 + startDelay, loop);
    };

    // Doubled count too — 16 instead of 8 — staggered so there's always
    // a visible pixel mid-drift instead of all pulsing on the same beat.
    for (let i = 0; i < 16; i++) {
      createDriftPixel(i * 130);
    }

    // Tiny dino lineup parade across the bottom (one of each, idle).
    DINO_ORDER.forEach((id, i) => {
      const cx = GAME_WIDTH / 2 + (i - 1.5) * 36;
      const dino = this.add
        .sprite(cx, GAME_HEIGHT / 2 + 32, `dino-${id}`)
        .setScale(2)
        .play(`${id}-idle`)
        .setAlpha(0);
      this.tweens.add({
        targets: dino,
        alpha: 1,
        y: { from: GAME_HEIGHT / 2 + 24, to: GAME_HEIGHT / 2 + 32 },
        delay: 1100 + i * 120,
        duration: 320,
        ease: 'Back.easeOut',
      });
    });

    // ── Drifting Pixels easter egg hidden ──
    // The drifting pixels label, wave, ambient pixels, and hit zone are now hidden.
    // To restore, uncomment the code block below.
    // ...existing code...

    // Skip hint at the bottom.
    const skip = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 12, 'press any key to skip', {
        fontFamily: '"VT323", monospace',
        fontSize: '10px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({ targets: skip, alpha: 0.6, delay: 1500, duration: 500 });

    // Real CommsBox at the BOTTOM of the screen (under the dino lineup) so
    // it doesn't overlap with the bouncing-in title at the center. Mika
    // opens, Bosco answers — proper Star Fox 64 vibe.
    const comms = new CommsBox(this, GAME_HEIGHT - 70);
    this.time.delayedCall(1500, () => {
      comms.saySequence([
        { speaker: 'mika', text: 'Astrodinos, status report?' },
        { speaker: 'bosco', text: 'All paws on deck, captain.' },
      ]);
    });

    // Auto-advance to level select after the comms finish (~9s total).
    this.time.delayedCall(9000, () => this.advance());

    // Skip handlers.
    this.input.keyboard?.once('keydown', () => this.advance());
    this.input.once('pointerdown', () => this.advance());
  }

  private advance() {
    if (this.done) return;
    this.done = true;
    this.cameras.main.fade(280, 5, 7, 15);
    this.time.delayedCall(300, () => {
      this.scene.start('CharacterSelect');
    });
  }
}
